import React, { useEffect, useState } from 'react';
import { authApiClient } from '../../services/authApi';

// Independent verification of what Cron A runs landed in prod, written nightly by
// backend app/scripts/verify_corpus_landing.py. Every number here is re-derived
// from the databases; a value that could not be established arrives as null and is
// rendered as "—", never as 0.

export interface VerificationCheck {
  name: string;
  label: string;
  pipeline: number | null;
  db: number | null;
  ok: boolean | null;
}

export interface VerifiedDrug {
  set_id: string;
  name: string | null;
  created_at: string;
  state: string;
  issues: string[];
  prod_id: number | null;
  source_sections: number | null;
  source_chunks: number | null;
  prod_sections: number | null;
  prod_chunks: number | null;
  kind?: 'new' | 'update';
  from_version?: number | null;
  source_version?: number | null;
  prod_version?: number | null;
  pm_code?: string | null;
  missing_part?: 'din' | 'drug' | 'document' | null;
  prod_documents?: number | null;
  // informational, not an issue (e.g. Japan 'no_jp_drug': a PDF with no JP drug to link to, by design)
  note?: string | null;
}

export interface VerifiedRun {
  source: string;
  mode: 'merge' | 'direct';
  source_run_pk: number;
  run_id: string | null;
  started_at: string;
  completed_at: string | null;
  duration_s: number | null;
  run_status: string | null;
  discovered: number | null;
  reported_created: number | null;
  reported_failed: number | null;
  reported_not_parsed: number | null;
  found_created: number;
  landed: number;
  already_in_prod: number;
  pending_merge: number;
  not_in_prod: number;
  with_issues: number;
  check_status: 'ok' | 'pending' | 'warning' | 'error';
  checks: {
    checks: VerificationCheck[];
    issue_counts: Record<string, number>;
    kind_counts?: { new: number; update: number };
    reported_updated?: number | null;
    outdated_discovered?: number | null;
    reported_skipped?: number | null;
    // Cron A route when a corpus is fed by several routes (e.g. EPAR 'PAR' / 'SmOP')
    route?: string | null;
  } | null;
  drugs: { items: VerifiedDrug[]; truncated: boolean } | null;
  verified_at: string;
}

interface NamedDrug { set_id: string; prod_id: number | null; name: string | null; version?: number; missing_in_prod?: boolean }

export interface DayArrivals {
  merged_new_drugs: number;
  direct_new_drugs: number;
  new_versions: number | null;
  versions_superseded: number | null;
  new_dins?: number | null;
  names: { merged_new_drugs: NamedDrug[]; direct_new_drugs: NamedDrug[]; new_versions: NamedDrug[]; new_dins?: NamedDrug[] };
}

interface SourceNotes { runs: number; unattributed_drugs: { set_id: string; name: string | null; created_at: string }[]; overlapped_drugs: number }
export type VerificationNotes = { status_reasons?: string[] } & Record<string, SourceNotes | string[] | undefined>;

interface CoverageSample { set_id: string; title: string; version?: number | null; prod_version?: number; source_version?: number | null }

export interface VerificationSnapshot {
  verified_at: string;
  window_start: string;
  merge_boundary: string | null;
  status: 'ok' | 'pending' | 'warning' | 'error';
  duration_s: number | null;
  coverage_source: string | null;
  coverage_fetched_at: string | null;
  drugs_not_in_prod: number | null;
  versions_behind: number | null;
  parser_version_not_recorded: number | null;
  parser_version_older: number | null;
  coverage_error: string | null;
  coverage: {
    catalog?: { total_elements: number; total_pages?: number; unique_set_ids: number; pages?: number; reached_end?: boolean };
    by_route?: Record<string, number>;
    not_in_prod_sample?: CoverageSample[];
    versions_behind_sample?: CoverageSample[];
    prod_total?: number;
    parser?: { parser_version_current: number; current_versions: number; not_recorded: number; older: number; up_to_date: number };
  } | null;
  arrivals: Record<string, DayArrivals> | null;
  notes: VerificationNotes | null;
  stale: boolean;
}

export interface VerificationResponse {
  corpus: string;
  days: number;
  latest: VerificationSnapshot | null;
  runs: VerifiedRun[];
}

const DAY_OPTIONS = [3, 7, 14];

export const REGION_OPTIONS: { corpus: string; label: string }[] = [
  { corpus: 'us_spl', label: 'United States' },
  { corpus: 'ca_pm', label: 'Canada' },
  { corpus: 'uk_pil', label: 'United Kingdom' },
  { corpus: 'epar', label: 'European Union (EPAR)' },
  { corpus: 'jp_xml', label: 'Japan — package inserts' },
  { corpus: 'jp_pdf', label: 'Japan — leaflets, interview forms, RMPs' },
  { corpus: 'sba', label: 'United States — FDA review documents' },
  { corpus: 'au', label: 'Australia (TGA)' },
  { corpus: 'eu_smpc', label: 'European Union — product labels (SmPC)' },
  { corpus: 'jp_review', label: 'Japan — PMDA review reports' },
  { corpus: 'zh_pdf', label: 'China (NMPA/CDE)' },
];

type ArrivalKey = 'merged_new_drugs' | 'direct_new_drugs' | 'new_versions' | 'new_dins';

// Column headers are identical for every region; only the counting unit (shown as a
// caption and in tooltips) and the per-day name lists differ.
export const DAILY_COLUMNS: { key: 'merged_new_drugs' | 'direct_new_drugs' | 'label_updates'; header: string; title: string }[] = [
  { key: 'merged_new_drugs', header: 'New (via merge)', title: 'New items that arrived through the nightly buffer merge (each has a merge receipt)' },
  { key: 'direct_new_drugs', header: 'New (outside merge)', title: 'New items written straight into this database, without a merge receipt. Expected to be 0 on production.' },
  { key: 'label_updates', header: 'Label updates', title: 'Existing drugs updated to a newer label version (new version added and the old version retired). — means not applicable for this region.' },
];

export interface CorpusUiConfig {
  idLabel: string | null;
  // the per-product source document ('monograph', 'leaflet'); null = no separate document
  documentNoun: string | null;
  itemNoun: { one: string; many: string };
  xTitle: string;
  xUnit: string;
  yTitle: string;
  yUnavailable: string | null;
  zUnavailable: string | null;
  dailyUnit: string;
  dailyEmpty: string;
  dailyNames: { key: ArrivalKey; title: string; showVersion?: boolean }[];
  // why an item can never be merged, when that differs by region; null = the default
  notMergeableLabel?: string | null;
}

export const CORPUS_UI: Record<string, CorpusUiConfig> = {
  us_spl: {
    idLabel: null,
    documentNoun: null,
    itemNoun: { one: 'drug', many: 'drugs' },
    xTitle: 'New drugs in DailyMed, not in prod',
    xUnit: 'set_ids',
    yTitle: 'Newer label versions in DailyMed, not in prod',
    yUnavailable: null,
    zUnavailable: null,
    dailyUnit: 'Counted in drugs (DailyMed set_ids).',
    dailyEmpty: 'No new drugs or label updates on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'New drugs (via merge)' },
      { key: 'direct_new_drugs', title: 'New drugs (outside merge)' },
      { key: 'new_versions', title: 'Label updates', showVersion: true },
    ],
  },
  ca_pm: {
    idLabel: 'DIN',
    documentNoun: 'monograph',
    itemNoun: { one: 'DIN', many: 'DINs' },
    xTitle: 'DINs in Health Canada DPD, not in prod',
    xUnit: 'DINs',
    yTitle: 'Newer monograph revisions at Health Canada',
    yUnavailable: 'Not available for Canada: Health Canada does not publish monograph revision numbers.',
    zUnavailable: 'Not available for Canada: no parser version is recorded for Canadian monographs.',
    dailyUnit: 'Counted in completed product monographs (the merge copies monographs, not DINs). Label updates: not applicable — Health Canada does not publish monograph revision numbers. Click a day for the new DIN rows.',
    dailyEmpty: 'No monographs or DINs arrived on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'Monographs merged' },
      { key: 'direct_new_drugs', title: 'Monographs outside merge' },
      { key: 'new_dins', title: 'New DINs in prod' },
    ],
  },
  uk_pil: {
    idLabel: 'emc product',
    documentNoun: 'leaflet',
    itemNoun: { one: 'drug', many: 'drugs' },
    xTitle: "Products in emc's latest-updates feed, not in prod",
    xUnit: 'products',
    yTitle: 'Newer leaflet revisions at emc',
    yUnavailable: 'Not available for the UK: emc does not publish leaflet revision numbers (every leaflet is stored as version 1).',
    zUnavailable: 'Not available for the UK: no parser version is recorded for UK leaflets.',
    dailyUnit: 'Counted in completed patient leaflets (one per emc product). Other documents stored with UK leaflets, such as NICE guidance, are not counted. Label updates: not applicable — emc does not publish revision numbers.',
    dailyEmpty: 'No UK leaflets arrived on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'Leaflets merged' },
      { key: 'direct_new_drugs', title: 'Leaflets outside merge' },
    ],
  },
  epar: {
    idLabel: 'EMA product',
    documentNoun: 'document',
    itemNoun: { one: 'document', many: 'documents' },
    xTitle: 'EPAR documents in the EMA catalog, not in prod',
    xUnit: 'authorised products',
    yTitle: 'Newer EPAR revisions at EMA',
    yUnavailable: 'Not available for EPAR: no document revision is stored, and the merge only copies documents prod does not have.',
    zUnavailable: 'Not available for EPAR: no parser version is recorded for EPAR documents.',
    dailyUnit: 'Counted in completed EPAR documents (PAR = public assessment report, SmOP = summary of positive opinion). EPAR documents are not linked to a drug. Label updates: not applicable.',
    dailyEmpty: 'No EPAR documents arrived on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'Documents merged' },
      { key: 'direct_new_drugs', title: 'Documents outside merge' },
    ],
  },
  jp_xml: {
    idLabel: 'YJ / insert no.',
    documentNoun: 'package insert',
    itemNoun: { one: 'package insert', many: 'package inserts' },
    xTitle: 'Products in the PMDA manifest without a package insert in prod',
    xUnit: 'products (YJ codes)',
    yTitle: 'Newer package insert revisions at PMDA',
    yUnavailable: 'Not available for Japan: the PMDA manifest carries no revision numbers, and Cron A only fetches products prod does not have.',
    zUnavailable: 'Not available for Japan: no parser version is recorded for Japanese package inserts.',
    dailyUnit: 'Counted in package inserts (one XML label per YJ code and insert number). A document the merge finds already in prod is not copied again, so it gets no merge receipt and is not counted here. Label updates: not applicable.',
    dailyEmpty: 'No Japanese package inserts arrived on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'Package inserts merged' },
      { key: 'direct_new_drugs', title: 'Package inserts outside merge' },
    ],
  },
  jp_pdf: {
    idLabel: 'PMDA code',
    documentNoun: 'document',
    itemNoun: { one: 'document', many: 'documents' },
    xTitle: 'PDFs in the PMDA manifest, not completed in prod',
    xUnit: 'documents',
    yTitle: 'Newer PDF revisions at PMDA',
    yUnavailable: 'Not available for Japan: the PMDA manifest carries no revision numbers, and Cron A only fetches documents prod does not have.',
    zUnavailable: 'Not available for Japan: no parser version is recorded for Japanese PDFs.',
    dailyUnit: 'Counted in PDF documents: patient leaflets, interview forms and RMPs (review reports are not part of Cron A). A document the merge finds already in prod gets no merge receipt and is not counted. Label updates: not applicable.',
    dailyEmpty: 'No Japanese PDFs arrived on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'Documents merged' },
      { key: 'direct_new_drugs', title: 'Documents outside merge' },
    ],
  },
  sba: {
    idLabel: 'application / file',
    documentNoun: 'review document',
    itemNoun: { one: 'document', many: 'documents' },
    xTitle: "Review PDFs in the FDA manifest, not in prod",
    xUnit: 'review documents',
    yTitle: 'Newer review revisions at FDA',
    yUnavailable: 'Not available for FDA reviews: FDA does not publish revisions of a review document — a review is issued once.',
    zUnavailable: 'Not available for FDA reviews: no parser version is recorded for review documents.',
    dailyUnit: 'Counted in review documents (one per application number and source PDF). A document the merge finds already in prod is not copied again, so it gets no merge receipt and is not counted here. Label updates: not applicable.',
    dailyEmpty: 'No FDA review documents arrived on this day.',
    notMergeableLabel: 'Written, but no merge copies this status',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'Review documents merged' },
      { key: 'direct_new_drugs', title: 'Review documents outside merge' },
    ],
  },
  au: {
    idLabel: 'TGA code',
    documentNoun: 'document',
    itemNoun: { one: 'document', many: 'documents' },
    xTitle: 'AU documents at the TGA, not in this database',
    xUnit: 'documents',
    yTitle: 'Newer TGA revisions',
    yUnavailable: 'Not available for Australia: the TGA publishes a revision under a NEW code, so a revision arrives as a new document and there is no "versions behind" number.',
    zUnavailable: 'Not available for Australia: no parser version is recorded for AU documents.',
    dailyUnit: 'Counted in completed AU documents (PI = product information for prescribers, CMI = consumer medicine information). The nightly merge does not run the AU family, so every AU document is an arrival outside the merge. Label updates: not applicable.',
    dailyEmpty: 'No AU documents arrived on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'Documents merged' },
      { key: 'direct_new_drugs', title: 'Documents outside merge' },
    ],
  },
  eu_smpc: {
    idLabel: 'EMA product',
    documentNoun: 'SmPC',
    itemNoun: { one: 'product', many: 'products' },
    xTitle: 'Authorised EMA medicines with no SmPC here',
    xUnit: 'authorised products',
    yTitle: 'Newer SmPC revisions at EMA',
    yUnavailable: 'Not available for EU product labels: EMA publishes no SmPC revision number, so there is no "versions behind" number to compute.',
    zUnavailable: 'Not available for EU product labels: no parser version is recorded for EU SmPCs.',
    dailyUnit: 'Counted in completed SmPCs (one per EMA product). This corpus has no buffer merge, so Cron A writes straight into this database and every arrival is counted outside the merge. Label updates: not applicable.',
    dailyEmpty: 'No EU SmPCs arrived on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'SmPCs merged' },
      { key: 'direct_new_drugs', title: 'SmPCs written here' },
    ],
  },
  jp_review: {
    idLabel: 'PMDA code',
    documentNoun: 'review report',
    itemNoun: { one: 'review report', many: 'review reports' },
    xTitle: 'Review submissions in the source archive with no copy here',
    xUnit: 'review submissions',
    yTitle: 'Newer review revisions at PMDA',
    yUnavailable: 'Not available for PMDA review reports: a review is filed once and never revised, so there is no "versions behind" number to compute.',
    zUnavailable: 'Not available for PMDA review reports: no parser version is recorded for them.',
    dailyUnit: 'Counted in completed review reports (one per PMDA submission, covering a whole molecule). This corpus has no buffer merge, so every arrival is counted outside the merge. Label updates: not applicable — a review is never revised.',
    dailyEmpty: 'No review reports arrived on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'Review reports merged' },
      { key: 'direct_new_drugs', title: 'Review reports written here' },
    ],
  },
  zh_pdf: {
    idLabel: 'CDE acceptid',
    documentNoun: 'document',
    itemNoun: { one: 'document', many: 'documents' },
    xTitle: 'Products at CDE, not in prod',
    xUnit: 'products',
    yTitle: 'Newer document revisions at CDE',
    yUnavailable: 'Not available for China: CDE publishes no revision numbers, and every Chinese document is stored as version 1.',
    zUnavailable: 'Not available for China: no parser version is recorded for Chinese documents.',
    dailyUnit: 'Counted in completed Chinese documents (package insert 说明书 and review report 技术审评报告 — a product can have one of each). Chinese documents arrive with no product link, so they are not searchable as a medicine. Label updates: not applicable.',
    dailyEmpty: 'No Chinese documents arrived on this day.',
    dailyNames: [
      { key: 'merged_new_drugs', title: 'Documents merged' },
      { key: 'direct_new_drugs', title: 'Documents outside merge' },
    ],
  },
};

export const corpusUi = (corpus: string): CorpusUiConfig => CORPUS_UI[corpus] ?? CORPUS_UI['us_spl']!;

export const STATE_LABEL: Record<string, string> = {
  landed: 'Landed in prod',
  already_in_prod: 'In prod',
  pending_merge: 'Waiting for next merge',
  not_in_prod: 'NOT in prod',
  receipt_orphan: 'Merge receipt but drug missing',
  in_db: 'In this database',
  source_failed: 'Ingestion failed — not merged',
  not_mergeable: 'Not mergeable: no storage path (no product identity)',
  in_progress: 'Still being ingested — not searchable yet',
  reaches_no_drug: 'Stored but attached to no drug — no reader can reach it',
};

const capital = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

export const drugStateLabel = (d: VerifiedDrug, ui?: CorpusUiConfig, mode?: string): string => {
  if (d.state === 'not_mergeable' && ui?.notMergeableLabel) return ui.notMergeableLabel;
  if (d.state === 'source_failed' && ui?.documentNoun) {
    return `${capital(ui.documentNoun)} ingestion failed${mode === 'direct' ? ' — not searchable' : ' in buffer — not merged'}`;
  }
  if (d.missing_part === 'drug' && ui?.documentNoun) {
    if (d.state === 'not_in_prod') return `Drug row not in prod (${ui.documentNoun} is)`;
    if (d.state === 'pending_merge') return 'Drug row awaiting merge';
  }
  if (d.missing_part === 'document' && ui?.documentNoun) {
    if (d.state === 'not_in_prod') return `NOT in prod (no completed ${ui.documentNoun})`;
  }
  if (d.missing_part === 'din') {
    if (d.state === 'not_in_prod') return 'DIN not in prod (monograph is)';
    if (d.state === 'pending_merge') return 'DIN awaiting merge';
  }
  if (d.kind === 'update') {
    if (d.state === 'not_in_prod') return `Not updated in prod (prod v${d.prod_version ?? '?'})`;
    if (d.state === 'pending_merge') return 'Update awaiting merge';
    if (d.state === 'already_in_prod') return 'Updated in prod';
  }
  return STATE_LABEL[d.state] ?? d.state;
};

export const ISSUE_LABEL: Record<string, string> = {
  no_sections: 'no sections',
  no_chunks: 'no search chunks',
  differs_from_source: 'section/chunk count differs from buffer',
  receipt_points_to_other_drug: 'merge receipt points to another drug',
  duplicate_document: 'document exists more than once in prod',
  no_drug_link: 'document not linked to a drug',
  linked_to_other_drug: 'document linked to a different drug',
  hash_identity: 'content hash instead of product name in storage path',
  no_pages: 'no page images',
  no_source_path: 'no storage path',
  version_key_mismatch: 'stored under a different version key than the merge looks for',
  review_pinned_to_one_drug: 'pinned to a single drug — a review covers a whole molecule',
  slot_blocked_by_unusable_row: 'a row for this document exists here but never completed, so the merge skips it instead of replacing it',
};

export const NOTE_LABEL: Record<string, string> = {
  no_jp_drug: 'no JP drug to link to (expected: PDFs never create drugs)',
  no_drug_match: 'not linked to a drug (expected: a review links only when its application number matches one)',
  not_parsed: 'stored but not parsed',
  cost_capped: 'ingestion stopped at the cost cap',
  superseded: 'superseded by another document',
  in_progress: 'still pending or processing',
  molecule_present: 'its molecule IS in this database — the link is missing, not impossible',
  molecule_absent: 'its molecule is not in this database, so there is nothing to link it to',
  unlinked_by_merge: 'no product link (expected: the merge copies Chinese documents without a drug)',
};

export const issueLabel = (issue: string, ui?: CorpusUiConfig): string => {
  const label = ISSUE_LABEL[issue] ?? issue;
  return ui?.documentNoun ? label.replace('document', ui.documentNoun) : label;
};

const STATUS_STYLE: Record<string, string> = {
  ok: 'bg-green-50 text-green-700 border-green-200',
  pending: 'bg-slate-50 text-slate-600 border-slate-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  error: 'bg-red-50 text-red-700 border-red-200',
};
const STATUS_TEXT: Record<string, string> = { ok: 'Verified', pending: 'Awaiting merge', warning: 'Needs review', error: 'Failed' };

const fmt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString());
const utc = (iso: string | null | undefined) => (iso ? `${iso.slice(0, 16).replace('T', ' ')} UTC` : '—');
export const dur = (s: number | null) => (s === null ? '—' : s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
  : s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${STATUS_STYLE[status] ?? STATUS_STYLE['pending']}`}>
    {STATUS_TEXT[status] ?? status}
  </span>
);

const CheckMark: React.FC<{ ok: boolean | null }> = ({ ok }) =>
  ok === null ? <span className="text-slate-400" title="Not reported">—</span>
    : ok ? <span className="text-green-700" aria-label="pass">✓</span>
      : <span className="text-red-700" aria-label="fail">✗</span>;

const CoverageCard: React.FC<{
  testId: string; title: string; value: React.ReactNode; detail: React.ReactNode;
  sample?: CoverageSample[] | undefined; total?: number | null | undefined; sampleKind?: 'missing' | 'behind' | undefined;
}> = ({ testId, title, value, detail, sample, total, sampleKind }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid={testId}>
      <div className="text-xs text-slate-500">{title}</div>
      <div className="text-2xl font-semibold text-slate-800 font-mono mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{detail}</div>
      {sample && sample.length > 0 && (
        <>
          <button type="button" onClick={() => setOpen(o => !o)} className="mt-2 text-xs text-indigo-600 hover:underline">
            {open ? 'Hide list' : `Show list${total && total > sample.length ? ` (first ${sample.length} of ${total.toLocaleString()})` : ''}`}
          </button>
          {open && (
            <div className="mt-2 max-h-72 overflow-y-auto rounded border border-slate-200">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-right px-2 py-1.5 font-medium w-8">#</th>
                    <th className="text-left px-2 py-1.5 font-medium">Drug / Set ID</th>
                    {sampleKind === 'behind' && (
                      <th className="text-right px-2 py-1.5 font-medium whitespace-nowrap">Prod → DailyMed</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sample.map((s, i) => (
                    <tr key={s.set_id} className="odd:bg-white even:bg-slate-50/60 align-top">
                      <td className="px-2 py-1.5 text-right text-slate-400 tabular-nums">{i + 1}</td>
                      <td className="px-2 py-1.5 min-w-0">
                        <div className="text-slate-800 break-words" title={s.title}>{s.title || '—'}</div>
                        <div className="font-mono text-[11px] text-slate-400 break-all select-all">{s.set_id}</div>
                      </td>
                      {sampleKind === 'behind' && (
                        <td className="px-2 py-1.5 text-right whitespace-nowrap font-mono">
                          <span className="text-slate-500">v{s.prod_version ?? '?'}</span>
                          <span className="text-slate-400"> → </span>
                          <span className="text-indigo-700 font-medium">v{s.source_version ?? '?'}</span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const drugRowClass = (d: VerifiedDrug): string => {
  if (d.state === 'source_failed') return 'text-amber-700';
  return d.issues.length || d.state === 'not_in_prod' || d.state === 'receipt_orphan' ? 'text-red-700' : 'text-slate-700';
};

const RunDetail: React.FC<{ run: VerifiedRun; ui: CorpusUiConfig }> = ({ run, ui }) => {
  return (
    <div className="p-3 bg-slate-50 border border-slate-200 rounded text-xs space-y-3">
      <div>
        <div className="font-medium text-slate-700 mb-1">Checks</div>
        <table className="text-xs">
          <thead><tr className="text-slate-500"><th className="text-left pr-4">Check</th><th className="text-right pr-4">Expected</th><th className="text-right pr-4">Found</th><th /></tr></thead>
          <tbody>
            {(run.checks?.checks ?? []).map(c => (
              <tr key={c.name}>
                <td className="pr-4 py-0.5">{c.label}</td>
                <td className="pr-4 text-right font-mono">{fmt(c.pipeline)}</td>
                <td className="pr-4 text-right font-mono">{fmt(c.db)}</td>
                <td><CheckMark ok={c.ok} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(run.drugs?.items.length ?? 0) > 0 && (
        <div>
          <div className="font-medium text-slate-700 mb-1">
            {ui.itemNoun.many.charAt(0).toUpperCase() + ui.itemNoun.many.slice(1)} written by this run{run.drugs?.truncated ? ` (first ${run.drugs.items.length})` : ''}
          </div>
          <div className="overflow-x-auto">
            <table className="text-xs w-full">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left pr-3">Drug</th><th className="text-left pr-3">ID</th>
                  <th className="text-left pr-3">Type</th>
                  <th className="text-left pr-3">Where it is now</th>
                  <th className="text-right pr-3">Sections {run.mode === 'merge' ? 'buffer/prod' : ''}</th>
                  <th className="text-right pr-3">Chunks {run.mode === 'merge' ? 'buffer/prod' : ''}</th>
                  <th className="text-left">Issues</th>
                </tr>
              </thead>
              <tbody>
                {run.drugs!.items.map(d => (
                  <tr key={`${d.set_id}-${d.pm_code ?? ''}`} data-testid={`verified-drug-${d.set_id}`} className={drugRowClass(d)}>
                    <td className="pr-3 py-0.5">{d.name ?? '—'}</td>
                    <td className="pr-3 font-mono" data-testid="cell-id">
                      {ui.idLabel ? `${ui.idLabel} ` : ''}{d.set_id}
                      {d.pm_code && <span className="text-slate-500" data-testid="cell-monograph">{' '}· monograph {d.pm_code}</span>}
                    </td>
                    <td className="pr-3">{d.kind === 'update'
                      ? `Label update${d.from_version != null ? ` v${d.from_version}` : ''} → v${d.source_version ?? '?'}`
                      : `New ${ui.itemNoun.one}${d.source_version != null ? ` v${d.source_version}` : ''}`}</td>
                    <td className="pr-3">{drugStateLabel(d, ui, run.mode)}{d.prod_id ? ` (#${d.prod_id})` : ''}
                      {d.note && <span className="text-slate-500" data-testid="cell-note">{' '}· {NOTE_LABEL[d.note] ?? d.note}</span>}</td>
                    <td className="pr-3 text-right font-mono">{run.mode === 'merge' ? `${fmt(d.source_sections)}/${fmt(d.prod_sections)}` : fmt(d.prod_sections)}</td>
                    <td className="pr-3 text-right font-mono">{run.mode === 'merge' ? `${fmt(d.source_chunks)}/${fmt(d.prod_chunks)}` : fmt(d.prod_chunks)}</td>
                    <td>{d.issues.map(i => issueLabel(i, ui)).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const NameList: React.FC<{ title: string; items: NamedDrug[]; showVersion?: boolean }> = ({ title, items, showVersion }) =>
  items.length === 0 ? null : (
    <div>
      <div className="font-medium text-slate-700 mb-1">{title}</div>
      <ul className="font-mono text-slate-600 space-y-0.5 max-h-60 overflow-y-auto">
        {items.map(n => (
          <li key={`${n.set_id}-${n.version ?? ''}`} className={n.missing_in_prod ? 'text-red-700' : ''}>
            {n.name ?? '—'} · {n.set_id}{showVersion ? ` · v${n.version}` : ''}{n.prod_id ? ` · #${n.prod_id}` : ''}
            {n.missing_in_prod ? ' · receipt exists but drug missing' : ''}
          </li>
        ))}
      </ul>
    </div>
  );

export const CorpusVerificationPanel: React.FC<{ corpus: string }> = ({ corpus: initialCorpus }) => {
  const [corpus, setCorpus] = useState(initialCorpus);
  const [days, setDays] = useState(7);
  const [data, setData] = useState<VerificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [showEmptyRuns, setShowEmptyRuns] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authApiClient
      .get<VerificationResponse>(`/admin/corpus-verification?corpus=${corpus}&days=${days}`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load verification'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [corpus, days]);

  const changeCorpus = (next: string) => {
    setCorpus(next);
    setExpandedRun(null);
    setExpandedDay(null);
  };

  const controls = (
    <div className="ml-auto flex items-center gap-3">
      <div className="flex items-center">
        <label className="text-xs text-slate-500 mr-2" htmlFor="verification-region">Region</label>
        <select id="verification-region" value={corpus} onChange={e => changeCorpus(e.target.value)}
          className="text-sm border border-slate-200 rounded px-2 py-1 bg-white">
          {REGION_OPTIONS.map(o => <option key={o.corpus} value={o.corpus}>{o.label}</option>)}
        </select>
      </div>
      <div className="flex items-center">
        <label className="text-xs text-slate-500 mr-2" htmlFor="verification-days">Range</label>
        <select id="verification-days" value={days} onChange={e => setDays(Number(e.target.value))}
          className="text-sm border border-slate-200 rounded px-2 py-1 bg-white">
          {DAY_OPTIONS.map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>
    </div>
  );

  if (loading) return <div className="px-4 py-8 text-center text-slate-400">Loading…</div>;
  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">{controls}</div>
        <div className="px-4 py-8 text-center text-red-600">{error}</div>
      </div>
    );
  }

  const latest = data?.latest ?? null;
  if (!latest) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">{controls}</div>
        <div className="bg-white rounded-lg border border-slate-200 px-4 py-8 text-center text-slate-500" data-testid="verification-empty">
          Not verified yet — no verification has been recorded for this corpus.
        </div>
      </div>
    );
  }

  const ui = corpusUi(corpus);
  const runs = data?.runs ?? [];
  const emptyRuns = runs.filter(r => r.found_created === 0 && (r.reported_created ?? 0) === 0 && r.check_status === 'ok');
  const visibleRuns = showEmptyRuns ? runs : runs.filter(r => !emptyRuns.includes(r));
  const parser = latest.coverage?.parser;
  const unattributed = Object.entries(latest.notes ?? {})
    .filter(([k]) => k !== 'status_reasons')
    .flatMap(([source, n]) => ((n as SourceNotes | undefined)?.unattributed_drugs ?? []).map(d => ({ ...d, source })));
  const catalog = latest.coverage?.catalog;
  const arrivalDays = Object.keys(latest.arrivals ?? {}).sort().reverse().slice(0, days);

  return (
    <div className="space-y-4" data-testid="verification-panel">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <StatusBadge status={latest.status} />
        <span>Last verified <span className="font-mono">{utc(latest.verified_at)}</span></span>
        <span className="text-slate-400">· checked against this environment's database{latest.merge_boundary ? `, last merge ${utc(latest.merge_boundary)}` : ''}</span>
        {controls}
      </div>
      {(latest.notes?.status_reasons?.length ?? 0) > 0 && (
        <ul className="px-3 py-2 rounded border border-slate-200 bg-white text-xs text-slate-700 list-disc list-inside" data-testid="status-reasons">
          {latest.notes!.status_reasons!.map(r => <li key={r}>{r}</li>)}
        </ul>
      )}
      {unattributed.length > 0 && (
        <details className="px-3 py-2 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800" data-testid="unattributed">
          <summary>{unattributed.length} {ui.itemNoun.one}(s) created outside any recorded Cron A run — not verified against a run</summary>
          <ul className="mt-1 font-mono">
            {unattributed.map(d => <li key={`${d.source}-${d.set_id}`}>{d.source}: {d.name ?? '—'} · {d.set_id} · {utc(d.created_at)}</li>)}
          </ul>
        </details>
      )}
      {latest.stale && (
        <div className="px-3 py-2 rounded border border-amber-200 bg-amber-50 text-amber-800 text-xs" role="alert">
          This verification is out of date (last run {utc(latest.verified_at)}). The numbers below may not reflect the database today.
        </div>
      )}

      {/* Coverage: what is still missing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CoverageCard
          testId="coverage-x" title={ui.xTitle}
          value={fmt(latest.drugs_not_in_prod)}
          detail={latest.drugs_not_in_prod === null
            ? (latest.coverage_error ?? 'Not available')
            : <>Source: {latest.coverage_source} · fetched {utc(latest.coverage_fetched_at)}{catalog?.unique_set_ids != null ? ` · ${catalog.unique_set_ids.toLocaleString()} ${ui.xUnit}` : ''}{catalog?.reached_end === false ? ` · stopped at the ${catalog.pages ?? '?'}-page limit before the end of the feed` : ''}{latest.coverage?.by_route ? ` · ${Object.entries(latest.coverage.by_route).map(([k, v]) => `${k} ${v.toLocaleString()}`).join(' · ')}` : ''}</>}
          sample={latest.coverage?.not_in_prod_sample} total={latest.drugs_not_in_prod} sampleKind="missing"
        />
        {ui.yUnavailable ? (
          <CoverageCard testId="coverage-y" title={ui.yTitle} value="—" detail={ui.yUnavailable} />
        ) : (
          <CoverageCard
            testId="coverage-y" title={ui.yTitle}
            value={fmt(latest.versions_behind)}
            detail={latest.versions_behind === null
              ? (latest.coverage_error ?? 'Not available')
              : <>DailyMed version &gt; prod version · fetched {utc(latest.coverage_fetched_at)}</>}
            sample={latest.coverage?.versions_behind_sample} total={latest.versions_behind} sampleKind="behind"
          />
        )}
        {ui.zUnavailable ? (
          <CoverageCard testId="coverage-z" title="Parser version" value="—" detail={ui.zUnavailable} />
        ) : (
          <CoverageCard
            testId="coverage-z" title={`Drugs parsed with an older parser version (current: v${parser?.parser_version_current ?? '?'})`}
            value={fmt(latest.parser_version_older)}
            detail={<>
              <span title="drug_versions.parser_version is empty: that version was never re-checked by the reconcile loop, so the parser version it was built with is unknown. It is NOT counted as older.">
                Unknown (no parser version recorded): {fmt(latest.parser_version_not_recorded)}
              </span>
              {parser ? ` of ${parser.current_versions.toLocaleString()} current versions` : ''} · checked {utc(latest.verified_at)}
            </>}
          />
        )}
      </div>

      {/* Runs */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h4 className="text-xs font-semibold text-slate-700">Cron A runs</h4>
          {emptyRuns.length > 0 && (
            <label className="ml-auto text-xs text-slate-500 flex items-center gap-1">
              <input type="checkbox" checked={showEmptyRuns} onChange={e => setShowEmptyRuns(e.target.checked)} />
              Show {emptyRuns.length} run(s) that wrote nothing
            </label>
          )}
        </div>
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Run start (UTC)</th>
                <th className="text-right px-3 py-2 font-medium">Duration</th>
                <th className="text-right px-3 py-2 font-medium" title="New drugs and newer label versions waiting when the run started">Backlog</th>
                <th className="text-left px-3 py-2 font-medium" title="New drugs and label updates (existing drugs moved to a newer version) this run wrote, verified in the database. For Canada each DIN is one drug.">Drugs written</th>
                <th className="text-right px-3 py-2 font-medium" title="Failures reported by the run, and items it skipped">Failed / skipped</th>
                <th className="text-left px-3 py-2 font-medium" title="How many of this run's drugs are in the database at the run's version or newer">Reached prod</th>
                <th className="text-right px-3 py-2 font-medium" title="Drugs with no sections, no search chunks, or a copy that differs">Data issues</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleRuns.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">No runs in this range</td></tr>
              )}
              {visibleRuns.map(r => {
                const key = `${r.source}|${r.source_run_pk}`;
                const kinds = r.checks?.kind_counts ?? { new: r.found_created, update: 0 };
                const reportedCheck = r.checks?.checks.find(c => c.name === 'reported_vs_found');
                const backlog = r.discovered === null ? null : r.discovered + (r.checks?.outdated_discovered ?? 0);
                const reached = r.mode === 'merge' ? r.landed + r.already_in_prod : r.found_created;
                const sourceTag = r.source === 'buffer' ? null
                  : r.source === 'production' ? 'Prod VM' : r.source === 'staging' ? 'Staging VM' : r.source;
                return (
                  <React.Fragment key={key}>
                    <tr data-testid={`verified-run-${r.source}-${r.source_run_pk}`} className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setExpandedRun(expandedRun === key ? null : key)}>
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                        {utc(r.started_at)}
                        {r.checks?.route && <span className="ml-2 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-sans" data-testid="route-tag">{r.checks.route}</span>}
                        {sourceTag && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-sans" data-testid="source-tag">{sourceTag}</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">{dur(r.duration_s)}</td>
                      <td className="px-3 py-2 text-right font-mono"
                        title={r.checks?.outdated_discovered != null ? `${fmt(r.discovered)} new, ${fmt(r.checks.outdated_discovered)} label updates` : undefined}>{fmt(backlog)}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid="cell-written">
                        <span className="font-mono">{fmt(kinds.new)}</span> new
                        {kinds.update > 0 && <> · <span className="font-mono">{fmt(kinds.update)}</span> {kinds.update === 1 ? 'update' : 'updates'}</>}
                        {reportedCheck?.ok === false && (
                          <span className="text-amber-700" title="The run reported a different number than was found in the database">{' '}⚠ run reported {fmt(reportedCheck.pipeline)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap" data-testid="cell-failed">
                        {fmt(r.reported_failed)}
                        {(r.checks?.reported_skipped ?? 0) > 0 && <span className="text-slate-500">{' '}· {fmt(r.checks!.reported_skipped)} skipped</span>}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid="cell-reached">
                        <span className="font-mono">{fmt(reached)} of {fmt(r.found_created)}</span>
                        {r.mode === 'merge' && r.pending_merge > 0 && <span className="text-slate-500">{' '}· {fmt(r.pending_merge)} awaiting merge</span>}
                        {r.mode === 'merge' && r.not_in_prod > 0 && <span className="text-red-700 font-semibold">{' '}· {fmt(r.not_in_prod)} missing</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${r.with_issues ? 'text-amber-700' : ''}`}>{fmt(r.with_issues)}</td>
                      <td className="px-3 py-2"><StatusBadge status={r.check_status} /></td>
                    </tr>
                    {expandedRun === key && (
                      <tr><td colSpan={8} className="px-3 pb-3"><RunDetail run={r} ui={ui} /></td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* New drugs and label updates per day */}
      <div>
        <h4 className="text-xs font-semibold text-slate-700">New drugs and label updates per day (UTC)</h4>
        <div className="text-xs text-slate-500 mb-2" data-testid="daily-unit">{ui.dailyUnit}</div>
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Date (UTC)</th>
                {DAILY_COLUMNS.map(c => (
                  <th key={c.key} className="text-right px-3 py-2 font-medium" title={c.title}>{c.header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {arrivalDays.map(day => {
                const a = latest.arrivals?.[day];
                if (!a) return null;
                const mismatch = typeof a.new_versions === 'number' && typeof a.versions_superseded === 'number'
                  && a.new_versions !== a.versions_superseded;
                const dayTotal = DAILY_COLUMNS.reduce((sum, c) => {
                  const v = c.key === 'label_updates' ? a.new_versions : a[c.key];
                  return typeof v === 'number' ? sum + v : sum;
                }, 0);
                return (
                  <React.Fragment key={day}>
                    <tr data-testid={`arrivals-${day}`} className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setExpandedDay(expandedDay === day ? null : day)}>
                      <td className="px-3 py-2 font-mono text-xs">{day}</td>
                      {DAILY_COLUMNS.map(c => c.key === 'label_updates' ? (
                        <td key={c.key} className={`px-3 py-2 text-right font-mono ${mismatch ? 'text-amber-700 font-semibold' : ''}`}
                          data-testid={`label-updates-${day}`}
                          title={mismatch ? `Incomplete updates: ${a.new_versions} new version(s) added but ${a.versions_superseded} old version(s) retired` : undefined}>
                          {fmt(a.new_versions)}{mismatch && <span className="ml-1">⚠ {fmt(a.versions_superseded)} retired</span>}
                        </td>
                      ) : (
                        <td key={c.key} className="px-3 py-2 text-right font-mono">{fmt(a[c.key])}</td>
                      ))}
                    </tr>
                    {expandedDay === day && (
                      <tr><td colSpan={DAILY_COLUMNS.length + 1} className="px-3 pb-3">
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded text-xs space-y-3">
                          {ui.dailyNames.map(n => {
                            const items = a.names[n.key];
                            return items ? <NameList key={n.key} title={n.title} items={items} showVersion={n.showVersion === true} /> : null;
                          })}
                          {dayTotal === 0 && <div className="text-slate-400">{ui.dailyEmpty}</div>}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
